import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { applyAiFilters, clearAiFilters } from "../store/slices/aiSearchSlice";
import { updateFilters } from "../store/slices/homeFiltersSlice";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import Categories from "../components/common/Categories";
import Footer from "../components/common/Footer";
import Header from "../components/common/Header";
import GigsContainer from "../components/Home/GigsContainer";
import HomeAppleMapView from "../components/Home/HomeAppleMapView";
import HeroLandingSection from "../components/Home/HeroLandingSection";
import HowItWorksSection from "../components/Home/HowItWorksSection";
import BrowseCategoriesSection from "../components/Home/BrowseCategoriesSection";
import MarketplaceStatsSection from "../components/Home/MarketplaceStatsSection";
import JobsContainer from "../components/Home/JobsContainer";
import LocationFilter from "../components/Home/LocationFilter";
import MarketingPopup from "../components/Home/MarketingPopup";
import useGigsSelectorOnMap from "../store/hooks/useGigsSelectorOnMap";
import useHomeFilters from "../store/hooks/useHomeFilters";
import useJobsSelectorOnMap from "../store/hooks/useJobsSelectorOnMap";
import { getGigs } from "../store/thunks/gigsThunk";
import { colors } from "../utils/Colors";
import useProjectSelectorOnMap from "../store/hooks/useProjectSelectorOnMap";
import { useAuthContext } from "../features/authentication";
import axios from "../api/axios";
import { buildOrganizationLD, buildWebSiteLD, jsonLdString } from "../utils/seo";


const Home = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useAuthContext();

  // Save location for any logged-in user who doesn't have one yet
  useEffect(() => {
    if (!isAuthenticated || !user?._id) return;

    const loc = user?.location;
    if (loc?.city && loc?.country) {
      return;
    }

    const detect = async () => {
      try {
        const res = await fetch("https://ipapi.co/json/");
        const data = await res.json();
        if (data?.latitude && data?.longitude && !data?.error) {
          await axios.post("/users/save-location", {
            latitude: data.latitude,
            longitude: data.longitude,
            city: data.city || null,
            country: data.country_name || null,
            state: data.region || null,
          });
        }
      } catch (_) {
        // silently ignore
      }
    };

    detect();
  }, [isAuthenticated, user?._id]); // eslint-disable-line
  
  const {
    filters: { sectionFilter },
    handleUpdatingFilters,
  } = useHomeFilters();
  const { fetchGigs, fetchGigsNow, gigs, loading: gigsLoading } = useGigsSelectorOnMap();
  const {
    fetchJobs,
    fetchJobsNow,
    jobs: jobsData,
    loading: jobsLoading,
  } = useJobsSelectorOnMap();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    fetchProjects,
    fetchProjectsNow,
    projects:projectsData,
    loading: projectLoading,
  } = useProjectSelectorOnMap();
  const [showMap, setShowMap] = useState(true);
  const [showGigs, setShowGigs] = useState(false);
  const [aiNavigateTo, setAiNavigateTo] = useState(null);

  // ── Read AI filters from Redux ────────────────────────────────────────
  const aiReduxFilters = useSelector((state) => state.aiSearch?.filters || null);
  const aiIsActive     = useSelector((state) => state.aiSearch?.isActive || false);

  // ── Sync AI filters → URL (useEffect = always runs after render, no racing) ──
  const prevAiSearchText = useRef(null);
  useEffect(() => {
    const searchText = aiReduxFilters?.searchText || "";
    if (!aiIsActive || !searchText) return;
    if (prevAiSearchText.current === searchText) return; // skip if same
    prevAiSearchText.current = searchText;

    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("query",           searchText);
      p.set("gigs.searchText", searchText);
      p.set("jobs.searchText", searchText);
      p.set("searchText",      searchText);
      if (aiReduxFilters.sectionFilter) p.set("section", aiReduxFilters.sectionFilter);
      if (aiReduxFilters.minPrice)  p.set("minPrice",  String(aiReduxFilters.minPrice));
      if (aiReduxFilters.maxPrice)  p.set("maxPrice",  String(aiReduxFilters.maxPrice));
      if (aiReduxFilters.workType)  p.set("workType",  aiReduxFilters.workType);
      return p;
    });
  }, [aiIsActive, aiReduxFilters?.searchText]); // eslint-disable-line

  // ── Clear URL keys when AI filters are cleared ────────────────────────
  useEffect(() => {
    if (aiIsActive) return; // only run on clear
    if (prevAiSearchText.current === null) return; // never had AI search
    prevAiSearchText.current = null;
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete("query");
      p.delete("gigs.searchText");
      p.delete("jobs.searchText");
      p.delete("searchText");
      p.delete("minPrice");
      p.delete("maxPrice");
      p.delete("workType");
      return p;
    });
  }, [aiIsActive]); // eslint-disable-line

  const handleShowMap = () => {
    setShowMap(!showMap);
    setShowGigs(!showGigs);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("screen-view", showMap ? "list" : "map");
      return newParams;
    });
  };

  // Helper function to determine what to display with fallback logic
  const getDisplayContent = () => {
    const isGigsEnabled = sectionFilter.includes("gigs") || sectionFilter === "all";
    const isProjectsEnabled = sectionFilter.includes("projects") || sectionFilter === "all";
    const isJobsEnabled = sectionFilter.includes("jobs") || sectionFilter === "all";

    const hasGigs = gigs && gigs.length > 0;
    const hasProjects = projectsData && projectsData.length > 0;
    const hasJobs = jobsData && jobsData.length > 0;

    // Priority 1: Gigs (Sellers) - if enabled and has data
    if (isGigsEnabled && hasGigs) {
      return <GigsContainer mapGigs={gigs} gigsLoading={gigsLoading} />;
    }
    
    // Priority 2: Projects (Buyers) - if enabled and has data
    if (isProjectsEnabled && hasProjects) {
      return <JobsContainer mapJobs={projectsData} jobsLoading={projectLoading} isJobs={false}/>;
    }
    
    // Priority 3: Jobs (Employers) - if enabled and has data
    if (isJobsEnabled && hasJobs) {
      return <JobsContainer mapJobs={jobsData} jobsLoading={jobsLoading} isJobs={true}/>;
    }

    // Fallback: Show the first enabled type even if no data (to show "No data found" message)
    if (isGigsEnabled) {
      return <GigsContainer mapGigs={gigs} gigsLoading={gigsLoading} />;
    }
    
    if (isProjectsEnabled) {
      return <JobsContainer mapJobs={projectsData} jobsLoading={projectLoading} isJobs={false}/>;
    }
    
    if (isJobsEnabled) {
      return <JobsContainer mapJobs={jobsData} jobsLoading={jobsLoading} isJobs={true}/>;
    }
    
    // Default: Show gigs
    return <GigsContainer mapGigs={gigs} gigsLoading={gigsLoading} />;
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    dispatch(getGigs({ signal }));

    return () => {
      controller.abort();
    };
  }, [dispatch]);

  const handleAiFilters = async (parsed) => {
    // ── Build fetch params ──────────────────────────────────────────────
    const fetchParams = {
      searchText: parsed.subCategory || parsed.searchText || "",
      minPrice:   parsed.budgetMin  != null ? parsed.budgetMin  : "",
      maxPrice:   parsed.budgetMax  != null ? parsed.budgetMax  : "",
      workType:   parsed.workType   || "",
      limit: 200,
      page: 1,
    };

    // ── Normalise types to plural ────────────────────────────────────────
    const rawTypes = parsed.types?.length ? parsed.types : ["gig", "job", "project"];
    const types    = rawTypes.map((t) => (t.endsWith("s") ? t : t + "s"));
    const newSectionFilter = types.length === 1 ? types[0] : "all";

    // ── Store in Redux immediately ───────────────────────────────────────
    // URL sync happens via useEffect watching aiReduxFilters (no race condition)
    dispatch(applyAiFilters({
      searchText:    fetchParams.searchText,
      minPrice:      fetchParams.minPrice,
      maxPrice:      fetchParams.maxPrice,
      workType:      fetchParams.workType,
      types,
      sectionFilter: newSectionFilter,
      location:      parsed.location || null,
    }));

    // ── Update section filter in Redux for toggles ───────────────────────
    dispatch(updateFilters({ sectionFilter: newSectionFilter }));

    // ── Fetch results for each detected type ────────────────────────────
    if (types.includes("gigs"))     fetchGigsNow(fetchParams);
    if (types.includes("jobs"))     fetchJobsNow(fetchParams);
    if (types.includes("projects")) fetchProjectsNow(fetchParams);

    // ── Resolve coordinates + zoom spans ────────────────────────────────
    let lat     = parsed.location?.latitude;
    let lng     = parsed.location?.longitude;
    let latSpan = parsed.location?.latSpan;
    let lngSpan = parsed.location?.lngSpan;

    // Client-side fallback: use Google Maps Geocoder if backend didn't geocode
    if ((!lat || !lng) && parsed.location) {
      const locStr = [parsed.location.city, parsed.location.state, parsed.location.country]
        .filter(Boolean).join(", ");
      if (locStr && window.google?.maps?.Geocoder) {
        try {
          const result = await new Promise((resolve) => {
            new window.google.maps.Geocoder().geocode({ address: locStr }, (results, status) => {
              resolve(status === "OK" && results?.[0] ? results[0] : null);
            });
          });
          if (result) {
            lat = result.geometry.location.lat();
            lng = result.geometry.location.lng();
            const vp = result.geometry.viewport;
            if (vp) {
              const ne = vp.getNorthEast();
              const sw = vp.getSouthWest();
              latSpan = Math.abs(ne.lat() - sw.lat()) * 1.15;
              lngSpan = Math.abs(ne.lng() - sw.lng()) * 1.15;
            }
          }
        } catch (_) { /* silently ignore */ }
      }
    }

    const hasCoords = !!(lat && lng);
    const finalLatSpan = latSpan || 0.3;
    const finalLngSpan = lngSpan || 0.3;

    // ── Persist to localStorage so page refresh restores the search ──────
    try {
      localStorage.setItem("ai_search_state", JSON.stringify({
        ...parsed,
        location: {
          ...(parsed.location || {}),
          latitude:  lat  || null,
          longitude: lng  || null,
          latSpan:   hasCoords ? finalLatSpan : null,
          lngSpan:   hasCoords ? finalLngSpan : null,
        },
      }));
    } catch (_) { /* ignore quota errors */ }

    // ── Navigate map ─────────────────────────────────────────────────────
    if (hasCoords) {
      setAiNavigateTo({ lat, lng, latSpan: finalLatSpan, lngSpan: finalLngSpan });
      setShowMap(true);
      setShowGigs(false);
    } else {
      setShowMap(false);
      setShowGigs(true);
    }
  };

  // ── Restore AI search from localStorage on first mount ───────────────────
  const didRestoreRef = useRef(false);
  useEffect(() => {
    if (didRestoreRef.current) return;
    didRestoreRef.current = true;
    try {
      const saved = localStorage.getItem("ai_search_state");
      if (!saved) return;
      const savedParsed = JSON.parse(saved);
      if (savedParsed && (savedParsed.searchText || savedParsed.subCategory)) {
        handleAiFilters(savedParsed);
      }
    } catch (_) {}
  }, []); // eslint-disable-line

  return (
    <>
      <Helmet>
        <title>GigFalcon - Find Freelance Jobs and Services Near You</title>
        <meta
          name="description"
          content="GigFalcon is the marketplace to hire skilled freelancers, find local service providers and discover job opportunities. Post a project or gig today."
        />
        <link rel="canonical" href="https://www.gigfalcon.com/" />
        <meta property="og:title" content="GigFalcon - Find Freelance Jobs and Services Near You" />
        <meta property="og:description" content="GigFalcon is the marketplace to hire skilled freelancers, find local service providers and discover job opportunities." />
        <meta property="og:url" content="https://www.gigfalcon.com/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{jsonLdString(buildOrganizationLD())}</script>
        <script type="application/ld+json">{jsonLdString(buildWebSiteLD())}</script>
      </Helmet>
      <MarketingPopup />
      <Header />
      <HeroLandingSection />
      <Categories />
      <HomeStyled id="home-map-section" colors={colors}>
        <div className="d-block">
          <LocationFilter onAiFilters={handleAiFilters} onClearAiFilters={() => {
            localStorage.removeItem("ai_search_state");
            dispatch(clearAiFilters());
            dispatch(updateFilters({ sectionFilter: "all" }));
            fetchGigsNow({});
            fetchJobsNow({});
            fetchProjectsNow({});
          }} />
        </div>
        <div className="d-flex flex-row w-100">
          <div className={showMap ? "d-block" : "home-map-view d-none"}>
            {/* <HomeMapView /> */}
            <HomeAppleMapView
              filterGigs={fetchGigs}
              filterJobs={fetchJobs}
              filterProjects={fetchProjects}
              gigsLoading={gigsLoading}
              jobsLoading={jobsLoading}
              projectLoading={projectLoading}
              gigs={gigs}
              jobs={jobsData}
              projects={projectsData}
              navigateTo={aiNavigateTo}
            />
          </div>
          <div className={showGigs ? "d-block w-100" : "home-gigs d-none"}>
            {getDisplayContent()}
          </div>
        </div>
        <div className="show-map" onClick={handleShowMap}>
          {showMap ? (
            <p className="list-icon-text">List</p>
          ) : (
            <p className="list-icon-text">Map</p>
          )}
        </div>
      </HomeStyled>
      <HowItWorksSection />
      <BrowseCategoriesSection />
      <MarketplaceStatsSection />
      <Footer />
    </>
  );
};
export default Home;

const HomeStyled = styled.div`
  .list {
    width: 100% !important;
    height: auto;
  }
  .home-dropdown {
    padding: 20px;
  }
  .list-header h2 {
    font-size: 30px;
    text-align: center;
  }
  .list-header span {
    color: ${({ colors }) => colors.color.secondary};
  }
  .search-input-wrapper {
  }
  .search-bar {
    display: flex;
    align-items: center;
    margin: 20px auto;
    max-width: 600px;
  }
  .search-btn {
    background-color: ${({ colors }) => colors.color.secondary};
    color: ${({ colors }) => colors.color.white};
    border: none;
    padding: 8px 20px;
    border-radius: 0 5px 5px 0;
  }
  .home-gigs-list {
    padding: 20px;
  }
  .show-map {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    bottom: 35px;
    left: 50%;
    transform: translateX(-50%);
    box-shadow: rgba(0, 0, 0, 0.25) 0px 54px 55px,
      rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px,
      rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px;
    border-radius: 50%;
    background-color: ${({ colors }) => colors.color.white};
    width: 50px;
    height: 50px;
    cursor: pointer;
  }
  // .show-map img {
  //   width: 25px;
  //   height: 25px;
  //   color: ${({ colors }) => colors.color.secondary} !important;
  // }
  .list-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    cursor: pointer;
  }
  .list-icon {
    width: 20px;
    height: 20px;
    color: ${({ colors }) => colors.color.secondary} !important;
  }
  .list-icon-text {
    font-size: 16px;
    color: ${({ colors }) => colors.color.secondary} !important;
  }
  h2,
  h2 span {
    font-family: "MS_SemiBold";
  }

  @media only screen and (min-width: 480px) {
    .list-header h2 {
      font-size: 40px;
    }
    .home-gigs-list {
      padding: 40px !important;
      width: 100%;
    }
  }

  @media only screen and (min-width: 769px) {
    .list {
      overflow-y: scroll;
      height: 80vh;
      width: 100% !important;
      box-shadow: inset 0 1px 3px ${({ colors }) => colors.color.veryLightGrey};
    }
    .list::-webkit-scrollbar {
      width: 10px !important;
    }
    .list::-webkit-scrollbar-track {
      background-color: ${({ colors }) => colors.color.lightGrey};
    }
    .list::-webkit-scrollbar-thumb {
      background-color: ${({ colors }) => colors.color.secondary};
      opacity: 0.7;
    }
    .show-map {
      display: none !important;
    }
    .home-map-view {
      display: block !important;
    }
    .home-gigs {
      display: flex !important;
    }

    @media only screen and (max-width: 479px) {
      .list::-webkit-scrollbar {
        width: 20px !important;
      }
    }
  }
`;
